import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: string;
  kind: ToastKind;
  title: string;
  detail?: string;
}

interface ToastApi {
  success: (title: string, detail?: string) => void;
  error: (title: string, detail?: string) => void;
  info: (title: string, detail?: string) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DURATIONS: Record<ToastKind, number> = {
  success: 5000,
  info: 6000,
  // Errors stay longer: money went somewhere unexpected and the trader needs
  // time to read what happened.
  error: 10_000,
};

const MAX_VISIBLE = 4;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const remaining = useRef(new Map<string, number>());
  const startedAt = useRef(new Map<string, number>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    remaining.current.delete(id);
    startedAt.current.delete(id);
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const schedule = useCallback(
    (id: string, ms: number) => {
      remaining.current.set(id, ms);
      startedAt.current.set(id, Date.now());
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), ms),
      );
    },
    [dismiss],
  );

  const push = useCallback(
    (kind: ToastKind, title: string, detail?: string) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((current) => [...current.slice(-(MAX_VISIBLE - 1)), { id, kind, title, detail }]);
      schedule(id, DURATIONS[kind]);
    },
    [schedule],
  );

  /**
   * WCAG 2.2.1 (Timing Adjustable): a trader who looks away must not lose an
   * error about money. Hovering or keyboard-focusing the stack stops the
   * countdown; leaving it resumes from where it stopped.
   */
  const pause = useCallback(() => {
    for (const [id, timer] of timers.current) {
      clearTimeout(timer);
      const began = startedAt.current.get(id) ?? Date.now();
      const left = (remaining.current.get(id) ?? 0) - (Date.now() - began);
      remaining.current.set(id, Math.max(left, 0));
    }
    timers.current.clear();
  }, []);

  const resume = useCallback(() => {
    for (const [id, left] of [...remaining.current]) {
      if (timers.current.has(id)) continue;
      // Always leave a readable tail rather than dismissing the instant the
      // pointer leaves.
      schedule(id, Math.max(left, 1500));
    }
  }, [schedule]);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (title, detail) => push('success', title, detail),
      error: (title, detail) => push('error', title, detail),
      info: (title, detail) => push('info', title, detail),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} onPause={pause} onResume={resume} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return context;
}

const ICONS: Record<ToastKind, typeof Info> = {
  success: CheckCircle2,
  error: AlertTriangle,
  info: Info,
};

const ACCENTS: Record<ToastKind, string> = {
  success: 'text-ok-500',
  error: 'text-danger-500',
  info: 'text-info-500',
};

function ToastViewport({
  toasts,
  onDismiss,
  onPause,
  onResume,
}: {
  toasts: Toast[];
  onDismiss: (id: string) => void;
  onPause: () => void;
  onResume: () => void;
}) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      // Assertive for errors would interrupt a trader mid-count; polite is
      // correct here and the visual treatment carries the urgency.
      //
      // aria-atomic is false on the container and true on each toast, so adding
      // a fourth toast announces only that toast rather than re-reading the
      // whole stack.
      role="status"
      aria-live="polite"
      aria-atomic="false"
      aria-relevant="additions"
      onMouseEnter={onPause}
      onMouseLeave={onResume}
      onFocus={onPause}
      onBlur={onResume}
      // `safe-toast` lifts the stack clear of the mobile tab bar and the home
      // indicator; on desktop it collapses back to plain padding.
      className="safe-toast pointer-events-none fixed bottom-0 right-0 z-toast flex w-full max-w-sm flex-col gap-2 px-4 pt-4"
    >
      {toasts.map((toast) => {
        const Icon = ICONS[toast.kind];
        return (
          <div
            key={toast.id}
            aria-atomic="true"
            className={cn(
              'pointer-events-auto flex items-start gap-2.5 rounded-md border border-line',
              'bg-surface px-3 py-2.5 shadow-popover',
              'motion-safe:[animation:toast-in_var(--dur-base)_var(--ease-out)]',
            )}
          >
            <Icon
              size={15}
              strokeWidth={1.5}
              aria-hidden="true"
              className={cn('mt-0.5 shrink-0', ACCENTS[toast.kind])}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium leading-snug text-fg">{toast.title}</p>
              {toast.detail ? (
                <p className="mt-0.5 text-xs leading-snug text-fg-secondary">{toast.detail}</p>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              aria-label={`Закрыть: ${toast.title}`}
              // The glyph stays small; `tap-target` grows the hit area to 44x44
              // around it. Safe here because it is the only control in the
              // toast and the next toast is a full gap away.
              className="focus-ring tap-target -mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-fg-muted transition-colors duration-fast hover:bg-sunken hover:text-fg"
            >
              <X size={14} strokeWidth={1.5} aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
